/**
 * GRI-07 — helpers de contagem de resposta/indicador, compartilhados pelas
 * telas de questionário/auditoria (Questionnaire, SimpleCertification,
 * PillarCertification, DiagnosisType, PeriodicType e os RespondentView*).
 *
 * POR QUE EXISTE
 *   O contador "Respondidas X/Y" contava como respondida QUALQUER questão que
 *   tivesse uma linha em `question_response` — inclusive a linha fantasma de
 *   `value` vazio que a gravação por indicador chegava a criar no formulário.
 *   Resultado: responder 1 questão com 2 indicadores mostrava 3/5.
 *
 *   `respostaComConteudo` só conta quando há conteúdo de fato (texto, arquivo
 *   ou medida GRI). `totalIndicadores`/`indicadoresRespondidos` alimentam o
 *   círculo separado de indicadores.
 *
 * O payload varia entre telas: a resposta chega como `user_responses` (objeto),
 * `users_responses` (array, uma entrada por empresa do grupo) ou `users_responses`
 * (objeto único). `resolverResposta` cobre os três.
 */

function temValor(valor) {
  return String(valor ?? '').trim() !== '';
}

/** A resposta da questão para a empresa selecionada, nos 3 formatos de payload. */
export function resolverResposta(question, selectedCompanyId) {
  if (!question) return null;

  if (question.user_responses) return question.user_responses;

  const many = question.users_responses;
  if (Array.isArray(many)) {
    if (selectedCompanyId == null) return many[0] ?? null;
    return (
      many.find(
        (r) => r?.company?.id_company === Number(selectedCompanyId),
      ) ?? null
    );
  }
  return many ?? null;
}

/** A medida de algum indicador foi preenchida nesta resposta? */
export function respostaTemMedida(resposta) {
  return (resposta?.gri_values ?? []).some((m) => temValor(m?.value));
}

/**
 * A questão tem resposta com conteúdo real (texto, arquivo ou medida GRI)?
 * Linha fantasma (`value` vazio, sem arquivo e sem medida) devolve `false`.
 */
export function respostaComConteudo(question, selectedCompanyId) {
  const resposta = resolverResposta(question, selectedCompanyId);
  if (!resposta) return false;

  return (
    temValor(resposta.response) ||
    (resposta.response_file?.length ?? 0) > 0 ||
    respostaTemMedida(resposta)
  );
}

/** Total de indicadores GRI de uma lista de questões (soma dos `gri` de cada). */
export function totalIndicadores(questions) {
  return (questions ?? []).reduce(
    (n, q) => n + (q?.gri?.length ?? 0),
    0,
  );
}

/** Indicadores GRI com medida preenchida numa lista de questões. */
export function indicadoresRespondidos(questions, selectedCompanyId) {
  return (questions ?? []).reduce((n, q) => {
    const resposta = resolverResposta(q, selectedCompanyId);
    const preenchidas = (resposta?.gri_values ?? []).filter((m) =>
      temValor(m?.value),
    ).length;
    return n + preenchidas;
  }, 0);
}
